// Product deals for leads and sales: product + units + expected duration + priced lines.
// The server (App\Services\DealBuilder) prices every deal again on save; this file only
// mirrors its rules so the form can show the same totals while the user types.

export interface DealCharge {
  id: number;
  name: string;
  system_code: string | null;
  charge_type: 'one_time' | 'recurring';
  billing_period: string | null;
  period_months: number | null;
  per_unit: boolean;
  is_optional: boolean;
  included_by_default: boolean;
  default_price: number;
  vat_rate: number;
}

export interface DealProduct {
  id: number;
  name: string;
  kind: string;
  unit_label: string;
  needs_installation: boolean;
  free_service_days: number | null;
  is_gps: boolean;
  charges: DealCharge[];
  event_charges: { name: string; default_price: number }[];
}

export interface DealLineInput {
  included?: boolean;
  quantity?: number | string;
  unit_price?: number | string;
}

export interface Deal {
  jahaji_product_id: number | '';
  units: number | string;
  contract_months: number | string;
  lines: Record<string, DealLineInput>;
}

export interface PricedLine {
  charge: DealCharge;
  included: boolean;
  quantity: number;
  unitPrice: number;
  periods: number;
  amount: number;
  vat: number;
}

export const DEFAULT_CONTRACT_MONTHS = 12;

export function emptyDeal(products: DealProduct[] = []): Deal {
  const gps = products.find(p => p.is_gps) || products[0];
  return { jahaji_product_id: gps ? gps.id : '', units: 1, contract_months: DEFAULT_CONTRACT_MONTHS, lines: {} };
}

export function findProduct(products: DealProduct[], id: number | string | ''): DealProduct | null {
  return products.find(p => String(p.id) === String(id)) || null;
}

export function periodWord(period: string | null): string {
  return ({ Monthly: 'month', 'Quarter Yearly': 'quarter', 'Half Yearly': 'half-year', Yearly: 'year' } as Record<string, string>)[period || ''] || '';
}

/** Same rules as DealBuilder: optional lines only when ticked, recurring fees per whole period, VAT on top. */
export function priceDeal(product: DealProduct | null, deal: Deal): { lines: PricedLine[]; subtotal: number; vat: number; total: number; hasRecurring: boolean } {
  const units = Math.max(1, parseInt(String(deal.units || 1), 10) || 1);
  const months = parseInt(String(deal.contract_months || DEFAULT_CONTRACT_MONTHS), 10) || DEFAULT_CONTRACT_MONTHS;
  const lines: PricedLine[] = [];
  let subtotal = 0;
  let vat = 0;
  let hasRecurring = false;
  for (const charge of product ? product.charges : []) {
    const input = deal.lines[charge.id] || {};
    const included = charge.is_optional ? (input.included !== undefined ? !!input.included : charge.included_by_default) : true;
    const quantity = charge.per_unit ? units : Math.max(1, parseInt(String(input.quantity || 1), 10) || 1);
    const rawPrice = input.unit_price;
    const unitPrice = rawPrice !== undefined && rawPrice !== '' && rawPrice !== null ? Number(rawPrice) || 0 : charge.default_price;
    const periods = charge.charge_type === 'recurring' ? Math.ceil(months / (charge.period_months || 1)) : 1;
    const amount = included ? Math.round(quantity * unitPrice * periods * 100) / 100 : 0;
    const lineVat = included ? Math.round(amount * (charge.vat_rate || 0)) / 100 : 0;
    if (included) {
      subtotal += amount;
      vat += lineVat;
      hasRecurring = hasRecurring || charge.charge_type === 'recurring';
    }
    lines.push({ charge, included, quantity, unitPrice, periods, amount, vat: lineVat });
  }
  return { lines, subtotal, vat, total: subtotal + vat, hasRecurring };
}

/** A saved lead / sale (items + product id) as form state. */
export function dealFromSaved(saved: any, products: DealProduct[]): Deal {
  const deal: Deal = {
    jahaji_product_id: saved.jahaji_product_id || '',
    units: saved.units || saved.estimated_ships || 1,
    contract_months: saved.contract_months || DEFAULT_CONTRACT_MONTHS,
    lines: {},
  };
  // Server prefill (sale_prefill) already sends lines keyed by charge id.
  if (saved.lines && !Array.isArray(saved.lines)) {
    for (const id of Object.keys(saved.lines)) {
      deal.lines[id] = { ...saved.lines[id], included: saved.lines[id].included !== false && saved.lines[id].included !== 0 };
    }
    return deal;
  }
  const product = findProduct(products, deal.jahaji_product_id);
  for (const item of saved.items || []) {
    if (item.jahaji_product_charge_id) {
      deal.lines[item.jahaji_product_charge_id] = { included: true, quantity: item.quantity, unit_price: item.unit_price };
    }
  }
  for (const charge of product ? product.charges : []) {
    if (charge.is_optional && !deal.lines[charge.id]) {
      deal.lines[charge.id] = { included: false };
    }
  }
  return deal;
}

/** The deal fields to send to the API. */
export function dealPayload(deal: Deal): Record<string, any> {
  return {
    jahaji_product_id: deal.jahaji_product_id,
    units: deal.units,
    contract_months: deal.contract_months,
    lines: deal.lines,
  };
}
